/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  EuiButtonIcon,
  EuiFlexGroup,
  EuiSelect,
  EuiSkeletonText,
  EuiSpacer,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useGetProjectMetricsQuery } from '@modules/autoops-lib/hooks'
import { type DateRangeOption, DEFAULT_RANGE_OPTION } from '@modules/autoops-lib/constants'
import type { ProjectType } from '@modules/ui-types/projects'
import ElasticsearchCharts from '@modules/auto-ops-components/MetricsChart/ElasticsearchCharts'
import DefaultCharts from '@modules/auto-ops-components/MetricsChart/DefaultCharts'
import { generateProjectAutoOpsUrl } from '@modules/autoops-lib/util'
import { ViewInAutoOpsButton } from '@modules/auto-ops-components/ViewInAutoOpsButton/ViewInAutoOpsButton'

type AutoOpsMetricsChartProps = {
  projectId: string
  projectType: ProjectType
  organizationId: string
  region: string
  isDeleted?: boolean
}

const messages = defineMessages({
  last3Hours: {
    id: `project.time_range_options.last_3_hours`,
    defaultMessage: `Last 3 hours`,
  },
  last6Hours: {
    id: `project.time_range_options.last_6_hours`,
    defaultMessage: `Last 6 hours`,
  },
  last12Hours: {
    id: `project.time_range_options.last_12_hours`,
    defaultMessage: `Last 12 hours`,
  },
  last24Hours: {
    id: `project.time_range_options.last_24_hours`,
    defaultMessage: `Last 24 hours`,
  },
  last2Days: {
    id: `project.time_range_options.last_2_days`,
    defaultMessage: `Last 2 days`,
  },
  last7Days: {
    id: `project.time_range_options.last_7_days`,
    defaultMessage: `Last 7 days`,
  },
  last10Days: {
    id: `project.time_range_options.last_10_days`,
    defaultMessage: `Last 10 days`,
  },
  refreshButtonText: {
    id: 'project.auto-ops.refresh',
    defaultMessage: 'Refresh',
  },
})

const AutoOpsMetricsChart = ({
  projectId,
  projectType,
  organizationId,
  region,
  isDeleted = false,
}: AutoOpsMetricsChartProps) => {
  const isElasticsearch = projectType === 'elasticsearch'
  const [from, setFrom] = useState<DateRangeOption>(DEFAULT_RANGE_OPTION)
  const query = useGetProjectMetricsQuery({
    projectId,
    projectType,
    organizationId,
    region,
    range: { from, to: 'now' },
  })
  const { formatMessage } = useIntl()
  const timeRangeSelectorOptions: Array<{ value: DateRangeOption; text: string }> = [
    { value: 'now-3h/h', text: formatMessage(messages.last3Hours) },
    { value: 'now-6h/h', text: formatMessage(messages.last6Hours) },
    { value: 'now-12h/h', text: formatMessage(messages.last12Hours) },
    { value: 'now-24h/h', text: formatMessage(messages.last24Hours) },
    { value: 'now-48h/h', text: formatMessage(messages.last2Days) },
    { value: 'now-7d', text: formatMessage(messages.last7Days) },
    { value: 'now-10d', text: formatMessage(messages.last10Days) },
  ]

  if (query.isError) {
    return null
  }

  if (query.isLoading) {
    return (
      <Fragment>
        <EuiSkeletonText />
        <EuiSpacer size='xxl' />
      </Fragment>
    )
  }

  if (!query.isSuccess) {
    return null
  }

  const href = generateProjectAutoOpsUrl({
    region,
    organizationId,
    resourceId: projectId,
  })

  return (
    <Fragment>
      <EuiFlexGroup gutterSize='l'>
        <EuiTitle size='s'>
          <h2>
            <FormattedMessage
              id='project.heading.usage_and_performance'
              defaultMessage='Usage and performance'
            />
          </h2>
        </EuiTitle>
        <EuiFlexGroup gutterSize='s' responsive={false} justifyContent='flexEnd'>
          <div>
            <EuiSelect
              options={timeRangeSelectorOptions}
              value={from}
              disabled={isDeleted}
              onChange={(e) => setFrom(e.target.value as DateRangeOption)}
              aria-label='Time range selector'
            />
          </div>
          <EuiButtonIcon
            isLoading={query.isRefetching}
            aria-label={formatMessage(messages.refreshButtonText)}
            display='base'
            size='m'
            iconType='refresh'
            disabled={isDeleted}
            onClick={() => query.refetch()}
          />
          <ViewInAutoOpsButton href={href} projectId={projectId} disabled={isDeleted} />
        </EuiFlexGroup>
      </EuiFlexGroup>

      <EuiSpacer size='l' />

      {isElasticsearch && <ElasticsearchCharts data={query.data} />}
      {!isElasticsearch && <DefaultCharts data={query.data} />}

      <EuiSpacer size='xxl' />
    </Fragment>
  )
}

export default AutoOpsMetricsChart
